import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { formatPriceWithCurrency } from '../../../utils/vat';

// Create public axios instance (no auth)
const publicAxios = axios.create({
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

function ExtraWorkRequestCard({ request, token, onResponded }) {
  const { t } = useTranslation();
  const [responding, setResponding] = useState(false);
  const [note, setNote] = useState('');
  const [result, setResult] = useState(null);
  const [showNoteField, setShowNoteField] = useState(false);


  const handleRespond = async (action) => {
    setResponding(true);
    try {
      const response = await publicAxios.post(
        `/api/public_maintenance/${token}/extra_work/${request.id}/respond/`,
        { action, note }
      );
      setResult({ success: true, action });
      if (onResponded) onResponded(action);
    } catch (err) {
      setResult({ error: err.response?.data?.error || t('Error') });
    } finally {
      setResponding(false);
    }
  };

  // Already responded
  if (result?.success) {
    return (
      <Card
        sx={{
          mb: 2,
          borderRadius: '12px',
          border: '2px solid',
          borderColor: result.action === 'approve' ? 'success.main' : 'error.main',
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          {result.action === 'approve' ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('Thank you for approving')}
              </Typography>
              <Typography color="text.secondary">
                {t('We will proceed with the additional work')}
              </Typography>
            </>
          ) : (
            <>
              <CancelIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('Request declined')}
              </Typography>
              <Typography color="text.secondary">
                {t('The additional work will not be performed')}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: '12px',
        border: '2px solid',
        borderColor: 'warning.main',
      }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <HourglassEmptyIcon sx={{ color: 'warning.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('Additional work requires approval')}
          </Typography>
        </Box>

        {/* Workshop message */}
        {request.message && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 500, mb: 0.5 }}>
              {t('Message from workshop')}:
            </Typography>
            {request.message}
          </Alert>
        )}

        {/* Items */}
        <List dense disablePadding>
          {request.parts?.map((part, idx) => (
            <ListItem key={`part-${idx}`} sx={{ px: 0 }}>
              <ListItemText
                primary={part.name}
                secondary={`${part.quantity} × ${formatPriceWithCurrency(part.price_incl_vat || part.price)} (${t('incl. VAT')})`}
              />
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                {parseFloat(part.discount_percent || 0) > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', textDecoration: 'line-through' }}>
                      {formatPriceWithCurrency(part.original_line_total_incl_vat)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'success.main' }}>
                      {`\u2212${parseFloat(part.discount_percent)}%`}
                    </Typography>
                  </Box>
                )}
                <Typography sx={{ fontWeight: 600 }}>
                  {formatPriceWithCurrency(part.line_total_incl_vat || part.line_total)}
                </Typography>
              </Box>
            </ListItem>
          ))}
          {request.jobs?.map((job, idx) => (
            <ListItem key={`job-${idx}`} sx={{ px: 0 }}>
              <ListItemText
                primary={job.name}
                secondary={job.description || `${job.quantity} × ${formatPriceWithCurrency(job.price_incl_vat || job.price)} (${t('incl. VAT')})`}
              />
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                {parseFloat(job.discount_percent || 0) > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', textDecoration: 'line-through' }}>
                      {formatPriceWithCurrency(job.original_line_total_incl_vat)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'success.main' }}>
                      {`\u2212${parseFloat(job.discount_percent)}%`}
                    </Typography>
                  </Box>
                )}
                <Typography sx={{ fontWeight: 600 }}>
                  {formatPriceWithCurrency(job.line_total_incl_vat || job.line_total)}
                </Typography>
              </Box>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        {/* Total */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
            {t('Total')} ({t('incl. VAT')})
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '20px' }}>
            {formatPriceWithCurrency(request.total_incl_vat || request.total)}
          </Typography>
        </Box>

        {/* Note field */}
        {showNoteField && (
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder={t('Add a note (optional)')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ mb: 2 }}
          />
        )}

        {result?.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {result.error}
          </Alert>
        )}

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={() => handleRespond('deny')}
            disabled={responding}
            startIcon={responding ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {t('Decline')}
          </Button>
          <Button
            variant="contained"
            color="success"
            fullWidth
            onClick={() => handleRespond('approve')}
            disabled={responding}
            startIcon={responding ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {t('Approve')}
          </Button>
        </Box>

        {!showNoteField && (
          <Button
            size="small"
            onClick={() => setShowNoteField(true)}
            sx={{ mt: 1, display: 'block', mx: 'auto' }}
          >
            {t('Add a note')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default ExtraWorkRequestCard;
